/**
 * Surfaces in-app notifications as native browser notifications when the tab is
 * hidden. Purely additive: the in-app notification store remains the source of
 * truth; this hook just mirrors the newest entry to the OS when appropriate.
 *
 * Permission is never requested automatically — call {@link requestPermission}
 * from a user gesture (e.g. a button in Settings / the user menu).
 *
 * @module shared/hooks/useBrowserNotifications
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecentNotifications } from '@/shared/stores/notificationStore';

type PermissionState = NotificationPermission | 'unsupported';

interface UseBrowserNotificationsResult {
  readonly supported: boolean;
  readonly permission: PermissionState;
  requestPermission: () => Promise<void>;
}

function isSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function useBrowserNotifications(): UseBrowserNotificationsResult {
  const navigate = useNavigate();
  const notifications = useRecentNotifications();
  const supported = isSupported();
  const [permission, setPermission] = useState<PermissionState>(() =>
    supported ? Notification.permission : 'unsupported'
  );

  // Tracks the id of the most recent notification we've already seen, so we
  // only surface genuinely new ones. Seeded on mount BEFORE the display effect
  // runs (declaration order matters) so existing notifications aren't replayed.
  const lastShownId = useRef<string | null>(null);
  useEffect(() => {
    if (lastShownId.current === null && notifications.length > 0) {
      lastShownId.current = notifications[0]?.id ?? null;
    }
    // Intentionally runs every render to keep the seed correct across remounts.
  });

  const requestPermission = useCallback(async (): Promise<void> => {
    if (!supported) return;
    const result = await Notification.requestPermission();
    setPermission(result);
  }, [supported]);

  useEffect(() => {
    if (!supported) return;
    const latest = notifications[0];
    if (!latest) return;
    if (latest.id === lastShownId.current) return;

    const previousId = lastShownId.current;
    lastShownId.current = latest.id;

    // Don't replay the initial backlog (previousId === null means first paint).
    if (previousId === null) return;
    // Only surface when the tab is in the background.
    if (typeof document !== 'undefined' && !document.hidden) return;
    // Re-check live permission (user may have revoked it).
    if (Notification.permission !== 'granted') return;

    const link = typeof latest.link === 'string' ? latest.link : '/';
    const options: NotificationOptions = {
      body: latest.message,
      tag: latest.id,
      data: { link },
    };

    const showViaServiceWorker = async (): Promise<boolean> => {
      if (!('serviceWorker' in navigator)) return false;
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (!registration) return false;
        await registration.showNotification(latest.title, options);
        return true;
      } catch {
        return false;
      }
    };

    void showViaServiceWorker().then((shown) => {
      if (shown) return;
      try {
        const notification = new Notification(latest.title, options);
        notification.onclick = () => {
          window.focus();
          void navigate(link);
          notification.close();
        };
      } catch {
        /* swallow — best effort only */
      }
    });
  }, [notifications, supported, navigate]);

  return { supported, permission, requestPermission };
}
